import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { UserResponse } from '@shared/schemas';

import type { AdminRole } from '@/constants/adminRoles';

import type { AdminDraft } from '../../_shared/domain/administrator-draft.model';
import type { EditAdministratorError } from '../domain/edit-administrator-error';
import { initialState } from './edit-administrator.state';

/**
 * Edit-administrator slice. Reducers are pure state transitions only; the I/O
 * (load / update / archive) lives in the saga, which dispatches these lifecycle
 * transitions as it progresses.
 */
const slice = createSlice({
  name: 'editAdministrator',
  initialState,
  reducers: {
    // ── Load lifecycle (dispatched by the saga) ──
    loadStarted: (state) => {
      state.loadStatus = 'loading';
      state.error = null;
    },
    loadSucceeded: (state, action: PayloadAction<UserResponse>) => {
      const user = action.payload;
      state.user = user;
      state.draft = {
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        role: (user.admin_role ?? '') as AdminRole | '',
        linkedinUrl: user.linkedin_url ?? '',
      };
      state.loadStatus = 'loaded';
    },
    loadFailed: (state, action: PayloadAction<EditAdministratorError>) => {
      state.loadStatus = 'failed';
      state.error = action.payload;
    },
    /** A single field changed; clears that field's validation error. */
    fieldChanged: (
      state,
      action: PayloadAction<{ field: keyof AdminDraft; value: string }>,
    ) => {
      const { field, value } = action.payload;
      // `role` is a union (AdminRole | ''); the select only ever emits valid
      // values, so we write through a string view of the draft.
      (state.draft as Record<keyof AdminDraft, string>)[field] = value;
      delete state.validationErrors[field];
    },
    // ── Save lifecycle ──
    saveStarted: (state) => {
      state.saveStatus = 'saving';
      state.error = null;
      state.validationErrors = {};
    },
    saveSucceeded: (state) => {
      state.saveStatus = 'succeeded';
    },
    saveFailed: (state, action: PayloadAction<EditAdministratorError>) => {
      state.saveStatus = 'failed';
      state.error = action.payload;
      if (action.payload.kind === 'validation') {
        state.validationErrors = action.payload.fields;
      }
    },
    // ── Archive lifecycle (independent of save) ──
    archiveStarted: (state) => {
      state.archiveStatus = 'saving';
    },
    archiveSucceeded: (state, action: PayloadAction<string | null>) => {
      state.archiveStatus = 'succeeded';
      if (state.user) state.user.archived_at = action.payload;
    },
    archiveFailed: (state, action: PayloadAction<EditAdministratorError>) => {
      state.archiveStatus = 'failed';
      state.error = action.payload;
    },
    /** Reset everything to the initial state (on unmount / navigation away). */
    resetForm: () => initialState,
  },
});

export const editAdministratorActions = slice.actions;
export const editAdministratorReducer = slice.reducer;
